/**
 * [SEARCH-HIGHLIGHT 2026-09-29, user] NEW FILE — lights up the searched text inside a table's rows,
 * in the amber tint the user picked from the template (option 4; the colour lives in GlobalStyles
 * as ::highlight(dt-search)).
 *
 * Built on the browser's CSS Custom Highlight API: we hand the browser a list of text ranges and it
 * paints them. NOTHING in the DOM changes — no <mark> elements, no re-render — so cell renderers,
 * sorting, row memos, column resize and the "…" cut-off titles are all untouched. A browser without
 * the API simply shows no highlight.
 *
 * Only <tbody> text is searched (never the header labels). A MutationObserver re-marks the rows when
 * they change (new data, sort, live refresh, an inline edit), batched into one animation frame.
 * Every table that uses the hook adds its ranges to ONE shared highlight, since a page can have two
 * tables (Refunds + Disputes slide) and the CSS rule names a single highlight.
 */
import { useEffect, RefObject } from 'react';

const NAME = 'dt-search';
// Safety cap only. Measured: Payments, all months, "gmail" = 8,772 rows / 8,778 matches → 48 ms to walk +
// 2 ms to build the highlight (dev build), once per data change — so every real search is covered.
const MAX_RANGES = 20000;
const byTable = new Map<number, Range[]>();
let nextId = 1;

const supported = (): boolean =>
  typeof CSS !== 'undefined' && 'highlights' in CSS && typeof (window as any).Highlight === 'function';

function repaint(): void {
  const registry = (CSS as any).highlights as Map<string, unknown>;
  const all: Range[] = [];
  byTable.forEach((r) => { for (const x of r) all.push(x); });
  if (all.length) registry.set(NAME, new (window as any).Highlight(...all));
  else registry.delete(NAME);
}

function rangesFor(root: HTMLElement, query: string): Range[] {
  const out: Range[] = [];
  const needle = query.toLowerCase();
  const scope = root.querySelector('tbody') || root;
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node && out.length < MAX_RANGES; node = walker.nextNode()) {
    const text = (node.nodeValue || '').toLowerCase();
    for (let i = text.indexOf(needle); i !== -1 && out.length < MAX_RANGES; i = text.indexOf(needle, i + needle.length)) {
      const r = document.createRange();
      r.setStart(node, i);
      r.setEnd(node, i + needle.length);
      out.push(r);
    }
  }
  return out;
}

/**
 * @param ref    the table (or its wrapper) whose rows should be marked
 * @param query  the page's search text ('' / undefined = no highlight)
 * @param watch  anything that changes when the table itself mounts or swaps (e.g. the row array),
 *               so the hook re-attaches after a skeleton → table hand-off
 */
export function useSearchHighlight(ref: RefObject<HTMLElement | null>, query: string | undefined, watch?: unknown): void {
  useEffect(() => {
    if (!supported()) return undefined;
    const q = (query || '').trim();
    const root = ref.current;
    if (!q || !root) return undefined;
    const id = nextId++;
    let frame = 0;
    const mark = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { byTable.set(id, rangesFor(root, q)); repaint(); });
    };
    mark();
    const observer = new MutationObserver(mark);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      byTable.delete(id);
      repaint();
    };
  }, [ref, query, watch]);
}
